import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FaLock, FaEnvelope, FaPhone, FaChevronDown, FaCheck, FaCalendar, FaUser, FaBriefcase, FaCheckCircle, FaEye, FaEyeSlash } from 'react-icons/fa';
import Toast from '../components/Toast';
import * as authService from '../services/authService';
import * as masterService from '../services/masterService';

const Signup = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const signupType = searchParams.get('type'); // 'young-india', or 'professor'
  const roleParam = searchParams.get('role'); // 'phd', 'professor', or 'reviewer'

  const [formData, setFormData] = useState({
    role: '',
    name: '',
    dateOfBirth: '',
    university: '',
    university_id: null, // Store university ID for is_free_signup check
    college: '',
    state: '',
    phoneNumber: '',
    email: '',
    password: '',
    confirmPassword: '',
    department: '',
    stream: '',
    specialization: '',
    designation: '',
    otherUniversityName: '',
    otherDepartmentName: '',
    otherStreamName: '',
    otherSpecializationName: '',
    specializations: [], // For multiple selections
  });

  const [subjects, setSubjects] = useState([]);
  const [streams, setStreams] = useState([]);
  const [specializations, setSpecializations] = useState([]);

  const [searchUniversity, setSearchUniversity] = useState('');
  const [showUniversityDropdown, setShowUniversityDropdown] = useState(false);
  const [searchState, setSearchState] = useState('');
  const [showStateDropdown, setShowStateDropdown] = useState(false);

  const [searchDepartment, setSearchDepartment] = useState('');
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
  const [searchStream, setSearchStream] = useState('');
  const [showStreamDropdown, setShowStreamDropdown] = useState(false);
  const [searchSpecialization, setSearchSpecialization] = useState('');
  const [showSpecializationDropdown, setShowSpecializationDropdown] = useState(false);

  const [universityList, setUniversityList] = useState([]); // Will store {name, state} objects
  const [stateList, setStateList] = useState([]);

  // Use state-based filtered list for universities since it comes from API search
  const filteredUniversities = universityList;

  const filteredStates = stateList.filter(s =>
    s.toLowerCase().includes(searchState.toLowerCase())
  );

  const filteredDepartments = subjects.filter(s => {
    const name = s.name || s;
    // Show all options if the search text matches the selected item (user is viewing dropdown of selected item)
    const selectedName = subjects.find(sub => sub.id === formData.department)?.name;
    if (formData.department && searchDepartment === selectedName) {
      return true;
    }
    return typeof name === 'string' && name.toLowerCase().includes(searchDepartment.toLowerCase());
  });

  const filteredStreams = streams.filter(m => {
    const name = m.name || m;
    const streamSubjectId = m.subject_id || m.subject?.id;
    const matchesDepartment = !formData.department || streamSubjectId === formData.department;

    const selectedName = streams.find(st => st.id === formData.stream)?.name;
    if (formData.stream && searchStream === selectedName) {
      return matchesDepartment;
    }

    return typeof name === 'string' && name.toLowerCase().includes(searchStream.toLowerCase()) && matchesDepartment;
  });

  const filteredSpecs = specializations.filter(sp => {
    const name = sp.name || sp;
    const specStreamId = sp.stream_id || sp.stream?.id;
    const matchesStream = !formData.stream || specStreamId === formData.stream;

    const selectedName = specializations.find(s => s.id === formData.specialization)?.name;
    if (formData.specialization && searchSpecialization === selectedName) {
      return matchesStream;
    }

    return typeof name === 'string' && name.toLowerCase().includes(searchSpecialization.toLowerCase()) && matchesStream;
  });

  // Add "Other" option to departments
  const departmentsWithOther = [...filteredDepartments];
  if (!departmentsWithOther.some(d => (d.name || d) === 'Other')) {
    departmentsWithOther.push({ id: 'other', name: 'Other' });
  }

  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [verification, setVerification] = useState({
    phoneVerified: false,
    phoneOtp: '',
    phoneOtpSent: false,
    isPreAuthorized: false,
    emailVerified: false,
    emailOtp: '',
    emailOtpSent: false,
  });

  const [toast, setToast] = useState(null);
  const [isPhoneLoading, setIsPhoneLoading] = useState(false);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);

  // Get signup configuration based on URL parameter
  const config = React.useMemo(() => {
    switch (signupType) {
      case 'young-india':
        return {
          title: 'Apply as Young India',
          subtitle: 'Submit articles to Young India journal',
          allowedRoleCodes: ['graduate', 'post-graduate'],
          preSelectedRole: null,
        };
      case 'professor':
        return {
          title: 'Register as Researcher / Professor / Reviewer',
          subtitle: 'Join Paper Portal as an academic professional',
          allowedRoleCodes: ['phd', 'professor', 'reviewer', 'editorial'],
          preSelectedRole: null,
        };
      default:
        // No type specified - redirect to selection
        return {
          title: 'Choose Your Registration Type',
          subtitle: 'Select how you would like to join Paper Portal',
          allowedRoleCodes: null,
          preSelectedRole: null,
          showSelection: true, // Flag to show registration type selection
        };
    }
  }, [signupType]);

  // Master data loading functions moved to component scope for reuse
  const loadUniversitiesAndStates = async () => {
    try {
      const response = await masterService.getAllUniversities();
      let rawData = [];

      if (response.success && Array.isArray(response.data)) {
        rawData = response.data;
      } else if (Array.isArray(response)) {
        rawData = response;
      }

      if (rawData.length > 0) {
        // Extract unique state names for the state dropdown fallback
        const states = rawData.map(u => u.state).filter(Boolean);
        setStateList([...new Set(states)].sort());

        // Initial university list
        setUniversityList(rawData.slice(0, 20));
      }
    } catch (error) {
      console.error('Error loading universities:', error);
    }
  };

  const loadMasterData = async () => {
    try {
      const response = await masterService.masterSearchAll();
      const data = response.success ? response.data : response;

      if (data) {
        setSubjects(data.subjects || []);
        setStreams(data.streams || []);
        setSpecializations(data.specializations || []);
      }
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  };

  // Fetch roles from API
  useEffect(() => {
    const loadRoles = async () => {
      try {
        setLoadingRoles(true);
        const rolesData = await authService.fetchRoles();

        // Filter and normalize roles based on signup type
        let filteredRoles = rolesData.map(role => {
          let description = role.description;
          if (role.code === 'professor') description = 'Professor';
          if (role.code === 'reviewer') description = 'Reviewer';
          if (role.code === 'editorial') description = 'EB Member';

          return {
            ...role,
            description: description
          };
        });

        if (config.allowedRoleCodes) {
          filteredRoles = filteredRoles.filter(role =>
            config.allowedRoleCodes.includes(role.code)
          );
        }

        setRoles(filteredRoles);

        // Set pre-selected role if applicable from config or query param
        if (config.preSelectedRole && filteredRoles.length > 0) {
          const preSelectedRoleObj = filteredRoles.find(r => r.code === config.preSelectedRole);
          if (preSelectedRoleObj) {
            setFormData(prev => ({ ...prev, role: preSelectedRoleObj.id }));
          }
        } else if (roleParam && filteredRoles.length > 0) {
          // Map external parameters to internal role codes
          const targetCode = (roleParam === 'editorial' || roleParam === 'assistant-professor' || roleParam === 'ongoing-phd' || roleParam === 'researcher')
            ? (roleParam === 'editorial' ? (filteredRoles.find(r => r.code === 'editorial') ? 'editorial' : 'professor') : 'phd')
            : roleParam;

          const preSelectedRoleObj = filteredRoles.find(r => r.code === targetCode);
          if (preSelectedRoleObj) {
            // Map internal codes back to UI status values for the academicStatus select
            let statusForSelect = roleParam;
            if (targetCode === 'phd') statusForSelect = 'researcher';
            if (targetCode === 'editorial') statusForSelect = 'editorial';

            setFormData(prev => ({ ...prev, role: preSelectedRoleObj.id, academicStatus: statusForSelect }));
          }
        }
      } catch (error) {
        console.error('Error fetching roles:', error);
        showToast('Failed to load roles. Please refresh the page.', 'error');
        // Fallback roles in case API fails
        const fallbackRoles = [
          { id: '1', description: 'Graduate', code: 'graduate' },
          { id: '2', description: 'Post Graduate', code: 'post-graduate' },
          { id: '3', description: 'On-going PhD/PhD', code: 'phd' },
          { id: '4', description: 'Professor', code: 'professor' },
          { id: '5', description: 'Reviewer', code: 'reviewer' },
          { id: '6', description: 'EB Member', code: 'editorial' },
          { id: '7', description: 'User', code: 'user' },
        ];

        let filteredFallback = fallbackRoles;
        if (config.allowedRoleCodes) {
          filteredFallback = fallbackRoles.filter(role =>
            config.allowedRoleCodes.includes(role.code)
          );
        }

        setRoles(filteredFallback);
      } finally {
        setLoadingRoles(false);
      }
    };

    loadRoles();
    loadUniversitiesAndStates();
    loadMasterData();
  }, [signupType, config]); // Re-fetch when signup type or config changes

  // University Search Effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchUniversity && searchUniversity.length >= 2) {
        // Don't search if the search text matches the selected university exactly
        if (formData.university === searchUniversity) return;

        try {
          const response = await masterService.searchUniversities(searchUniversity);
          let results = [];
          if (response.success && Array.isArray(response.data)) {
            results = response.data;
          } else if (Array.isArray(response)) {
            results = response;
          }

          // Add "Other" to results
          if (!results.some(u => (u.name || u) === "Other")) {
            results.push({ name: "Other", state: "" });
          }
          setUniversityList(results);
          setShowUniversityDropdown(true);
        } catch (error) {
          console.error('Error searching universities:', error);
        }
      } else if (!searchUniversity) {
        // Reset to initial list when cleared
        loadUniversitiesAndStates();
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchUniversity, formData.university]);

  // Master Data Search Effect (Department, Stream, Specialization)
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      // Handle Department Search - only if we don't have a selection
      if (searchDepartment && searchDepartment.length >= 2 && !formData.department) {
        try {
          const response = await masterService.masterSearch(searchDepartment);
          const data = response.success ? response.data : response;
          if (data && data.subjects) setSubjects(data.subjects);
        } catch (error) {
          console.error('Error searching departments:', error);
        }
      }

      // Handle Stream Search - only if we don't have a selection
      if (searchStream && searchStream.length >= 2 && !formData.stream) {
        try {
          const response = await masterService.masterSearch(searchStream);
          const data = response.success ? response.data : response;
          if (data && data.streams) setStreams(data.streams);
        } catch (error) {
          console.error('Error searching streams:', error);
        }
      }

      // Handle Specialization Search - only if we don't have a selection
      if (searchSpecialization && searchSpecialization.length >= 2 && !formData.specialization) {
        try {
          const response = await masterService.masterSearch(searchSpecialization);
          const data = response.success ? response.data : response;
          if (data && data.specializations) setSpecializations(data.specializations);
        } catch (error) {
          console.error('Error searching specializations:', error);
        }
      }

      // Reset to full list if all search strings are empty
      if (!searchDepartment && !searchStream && !searchSpecialization) {
        loadMasterData();
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchDepartment, searchStream, searchSpecialization]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phoneNumber') {
      const numericValue = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: numericValue }));

      // Reset verification if phone changes
      setVerification(prev => ({
        ...prev,
        phoneVerified: false,
        phoneOtpSent: false,
        phoneOtp: '',
        isPreAuthorized: false
      }));
      return;
    }

    if (name === 'dateOfBirth') {
      let val = value.replace(/\D/g, '');
      if (val.length > 8) val = val.slice(0, 8);

      let formatted = '';
      if (val.length > 0) {
        formatted += val.slice(0, 2);
        if (val.length > 2) {
          formatted += '-' + val.slice(2, 4);
          if (val.length > 4) {
            formatted += '-' + val.slice(4, 8);
          }
        }
      }
      setFormData(prev => ({ ...prev, dateOfBirth: formatted }));
      return;
    }

    setFormData({
      ...formData,
      [name]: value,
    });

    if (name === 'email') {
      setVerification(prev => ({
        ...prev,
        emailVerified: false,
        emailOtpSent: false,
        emailOtp: ''
      }));
    }
  };

  const showToast = (message, type = 'success', persistent = false) => {
    setToast({
      message,
      type,
      duration: persistent ? null : 3000
    });
  };

  const clearToast = () => setToast(null);

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSendPhoneOtp = async () => {
    if (!formData.phoneNumber || formData.phoneNumber.length !== 10) {
      showToast('Please enter a valid 10-digit phone number', 'error');
      return;
    }

    setIsPhoneLoading(true);
    try {
      // First check if phone number exists (for all roles) and get authorization status
      const checkResponse = await authService.checkPhone(formData.phoneNumber);
      console.log('Check phone/auth response:', checkResponse);

      // If phone exists, the API should throw an error, but let's also check the response availability
      if (checkResponse && checkResponse.available === false) {
        showToast('Phone number already exists. Please login instead.', 'error');
        setIsPhoneLoading(false);
        return;
      }

      // Check if this is a Reviewer or Editorial Board signup
      const selectedRole = roles.find(r => r.id === formData.role);
      const isReviewerRole = (selectedRole && selectedRole.code === 'reviewer') || formData.academicStatus === 'reviewer';
      const isEditorialBoard = formData.academicStatus === 'editorial';
      const isRestrictedRole = isReviewerRole || isEditorialBoard;

      // Handle Authorization Logic
      const isActuallyAuthorized = checkResponse && checkResponse.is_authorized;

      // Note: No longer blocking. Non-authorized users will sign up as candidates.
      if (isActuallyAuthorized) {
        const authRole = checkResponse.authorized_role;
        console.log('User is pre-authorized for role:', authRole);

        setVerification(prev => ({
          ...prev,
          isPreAuthorized: true,
          authorizedRoleCode: authRole?.code
        }));

        showToast(`Phone number is authorized as ${authRole?.label || 'Reviewer/Board'}! Sending OTP...`, 'success');
      }

      // Send OTP
      await authService.sendPhoneOtp(formData.phoneNumber);

      setVerification(prev => ({ ...prev, phoneOtpSent: true }));
      showToast('OTP sent successfully!', 'success');
    } catch (error) {
      console.error('Error sending phone OTP:', error);
      showToast(error.message || 'Failed to send OTP. Please try again.', 'error');
    } finally {
      setIsPhoneLoading(false);
    }
  };

  const handleVerifyPhone = async () => {
    if (!verification.phoneOtp || verification.phoneOtp.length !== 6) {
      showToast('Please enter a valid 6-digit OTP', 'error');
      return;
    }

    setIsPhoneLoading(true);
    try {
      await authService.verifyPhoneOtp(formData.phoneNumber, verification.phoneOtp);

      setVerification(prev => ({ ...prev, phoneVerified: true }));
      clearToast();
      showToast('Phone number verified successfully!', 'success');
    } catch (error) {
      console.error('Error verifying phone OTP:', error);
      showToast(error.message || 'Failed to verify OTP. Please try again.', 'error');
    } finally {
      setIsPhoneLoading(false);
    }
  };

  const handleSendEmailOtp = async () => {
    if (!formData.email || !formData.email.includes('@')) {
      showToast('Please enter a valid email address', 'error');
      return;
    }

    setIsEmailLoading(true);
    try {
      // First check if email exists
      const checkResponse = await authService.checkEmail(formData.email);
      console.log('Check email response:', checkResponse);

      // If email exists, the API should throw an error, but let's also check the response
      if (checkResponse && (checkResponse.exists === true || checkResponse.available === false)) {
        showToast('Email already exists. Please login instead.', 'error');
        setIsEmailLoading(false);
        return;
      }

      // Send OTP
      const response = await authService.sendEmailOtp(formData.email);
      console.log('Email OTP Response:', response);

      setVerification(prev => ({ ...prev, emailOtpSent: true }));
      showToast('OTP sent to your email. Please check your inbox.', 'success', true);
    } catch (error) {
      console.error('Error sending email OTP:', error);
      showToast(error.message || 'Failed to send OTP. Please try again.', 'error');
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!verification.emailOtp || verification.emailOtp.length !== 6) {
      showToast('Please enter a valid 6-digit OTP', 'error');
      return;
    }

    setIsEmailLoading(true);
    try {
      await authService.verifyEmailOtp(formData.email, verification.emailOtp);

      setVerification(prev => ({ ...prev, emailVerified: true }));
      clearToast();
      showToast('Email verified successfully!', 'success');
    } catch (error) {
      console.error('Error verifying email OTP:', error);
      showToast(error.message || 'Failed to verify OTP. Please try again.', 'error');
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!verification.phoneVerified) {
      showToast('Please verify your phone number', 'error');
      return;
    }

    if (!formData.email || !isValidEmail(formData.email)) {
      showToast('Please enter a valid email address', 'error');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      showToast('Passwords do not match', 'error');
      return;
    }

    if (!formData.dateOfBirth || !formData.dateOfBirth.includes('-')) {
      showToast('Please enter Date of Birth in DD-MM-YYYY format', 'error');
      return;
    }

    const [day, month, year] = formData.dateOfBirth.split('-');
    if (!day || !month || !year || year.length !== 4) {
      showToast('Invalid Date of Birth format. Use DD-MM-YYYY', 'error');
      return;
    }

    if (parseInt(year) > 2005) {
      showToast('You must be born in 2005 or earlier', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      const selectedRole = roles.find(r => r.id === formData.role);
      const isReviewerRole = (selectedRole && selectedRole.code === 'reviewer') || formData.academicStatus === 'reviewer' || formData.academicStatus === 'editorial';
      const isEditorialBoard = formData.academicStatus === 'editorial';

      // Build payload with only non-null values
      const registrationData = {
        role_id: formData.role,
        full_name: formData.name,
        date_of_birth: `${year}-${month}-${day}`,
        university: formData.university === 'Other' ? formData.otherUniversityName : formData.university,
        // Send university_id so backend can check is_free_signup exemption
        ...(formData.university_id ? { university_id: formData.university_id } : {}),
        state: formData.state,
        phone_number: formData.phoneNumber,
        email: formData.email,
        password: formData.password,
        confirm_password: formData.confirmPassword,
        is_reviewer: isReviewerRole && verification.isPreAuthorized ? 1 : 0,
        is_editorial_board: isEditorialBoard && verification.isPreAuthorized ? 1 : 0,
      };

      // Add optional fields only if they have values
      if (formData.college) registrationData.college = formData.college;
      if (formData.designation) registrationData.designation = formData.designation;

      // Handle department/stream/specialization based on 'other' selection
      if (formData.department === 'other') {
        registrationData.other_department = formData.otherDepartmentName;
        registrationData.other_stream = formData.otherStreamName;
        registrationData.other_specialization = formData.otherSpecializationName;
      } else {
        if (formData.department) registrationData.subject_id = formData.department;
        if (formData.stream) registrationData.stream_id = formData.stream;

        // Handle specializations for professors/reviewers (multi-select)
        if (Array.isArray(formData.specializations) && formData.specializations.length > 0) {
          registrationData.specialization_ids = formData.specializations;
          registrationData.specialization_id = formData.specializations[0]; // Also send first one for backwards compatibility
        } else if (formData.specialization) {
          registrationData.specialization_id = formData.specialization;
        }
      }

      console.log('═══════════════════════════════════════');
      console.log('🚀 REGISTRATION PAYLOAD');
      console.log('═══════════════════════════════════════');
      console.log('Role ID:', registrationData.role_id);
      console.log('Full Name:', registrationData.full_name);
      console.log('Phone:', registrationData.phone_number);
      console.log('Email:', registrationData.email);
      console.log('University:', registrationData.university);
      console.log('State:', registrationData.state);
      console.log('Department ID:', registrationData.subject_id);
      console.log('Stream ID:', registrationData.stream_id);
      console.log('Specialization ID:', registrationData.specialization_id);
      console.log('Is Reviewer:', registrationData.is_reviewer);
      console.log('Is Editorial Board:', registrationData.is_editorial_board);
      console.log('Full Payload:', JSON.stringify(registrationData, null, 2));
      console.log('═══════════════════════════════════════');

      await authService.registerUser(registrationData);

      showToast('Account created successfully! Logging you in...', 'success');

      // Auto-login logic
      try {
        const loginData = {
          role_id: formData.role,
          username: formData.phoneNumber || formData.email,
          password: formData.password,
        };

        const loginResponse = await authService.loginUser(loginData);

        if (loginResponse.success) {
          const apiData = loginResponse.data || {};
          const userObj = apiData.user || apiData;

          const userData = {
            ...userObj, // Spread existing properties
            id: userObj.id || userObj.user_id, // Normalize ID
            role: userObj.role_code || userObj.role || (roles.find(r => r.id === formData.role)?.code) || 'user',
            full_name: userObj.full_name || userObj.name || formData.name || userObj.username, // Priority for Navbar
            name: userObj.name || formData.name || userObj.full_name, // Normalize Name
            is_reviewer: (!!userObj.is_reviewer || (isReviewerRole && verification.isPreAuthorized)) ? 1 : 0,
            is_editorial_board: (!!userObj.is_editorial_board || (isEditorialBoard && verification.isPreAuthorized)) ? 1 : 0,
            payment_status: userObj.payment_status || apiData.payment_status || 'PAID', // Persist payment_status for navbar restriction
            loginTime: new Date().toISOString(),
          };

          authService.storeSession(userData, null, null, true);
          window.dispatchEvent(new Event('userLogin'));

          // Determine redirect path: show payment modal if PENDING
          const redirectPath = userData.payment_status === 'PENDING' ? '/?showPayment=1' : '/';

          if (isReviewerRole || isEditorialBoard) {
            const isActuallyReviewer = userObj.is_reviewer === true || userObj.is_reviewer === 1;
            const isActuallyEditorial = userObj.is_editorial_board === true || userObj.is_editorial_board === 1;

            if ((isReviewerRole && isActuallyReviewer) || (isEditorialBoard && isActuallyEditorial) || verification.isPreAuthorized) {
              showToast('Welcome! You have been verified and logged in.', 'success');
              setTimeout(() => navigate(redirectPath), 1500);
            } else {
              setShowSuccessPopup(true);
              setTimeout(() => navigate(redirectPath), 4000);
            }
          } else {
            setTimeout(() => navigate(redirectPath), 1500);
          }
          return;
        }
        // Fallback for failed auto-login but successful reg
        if (isReviewerRole || isEditorialBoard) {
          if (!verification.isPreAuthorized) setShowSuccessPopup(true);
          setTimeout(() => navigate('/'), 4000);
        } else {
          setTimeout(() => navigate('/'), 2000);
        }
      } catch (loginError) {
        console.error('Auto-login failed:', loginError);
        // Still redirect to home if registration was successful
        if (isReviewerRole || isEditorialBoard) {
          if (!verification.isPreAuthorized) setShowSuccessPopup(true);
          setTimeout(() => navigate('/'), 4000);
        } else {
          setTimeout(() => navigate('/'), 2000);
        }
      }
    } catch (error) {
      console.error('Error during registration:', error);
      // Extract specific validation errors if available
      let errorMsg = 'Registration failed.Check your credentials and Please try again.';
      if (error.errors) {
        const firstError = Object.values(error.errors)[0];
        if (Array.isArray(firstError)) errorMsg = firstError[0];
        else if (typeof firstError === 'string') errorMsg = firstError;
      } else if (error.message) {
        errorMsg = error.message;
      }
      showToast(errorMsg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Build login link - always go to cleanup /login route
  const loginLink = '/login';

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => setToast(null)}
        />
      )}

      {/* Show Registration Type Selection if no type specified */}
      {config.showSelection ? (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-light-blue to-white p-4 sm:p-6">
          <div className="max-w-5xl w-full">
            <div className="text-center mb-12">
              <img src="/logo.png" alt="Paper Portal" className="h-20 mx-auto mb-6" />
              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">{config.title}</h1>
              <p className="text-xl text-gray-600">{config.subtitle}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Young India Card */}
              <Link
                to="/signup?type=young-india"
                className="group bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all transform hover:scale-105 border-2 border-transparent hover:border-green-500"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <FaUser className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">Apply as Young India</h3>
                <p className="text-gray-600 text-center mb-4">For Graduate & Post Graduate students submitting articles</p>
                <div className="flex items-center justify-center text-green-600 font-semibold group-hover:gap-2 transition-all">
                  <span>Get Started</span>
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                </div>
              </Link>

              {/* Academic Professional Card */}
              <Link
                to="/signup?type=professor"
                className="group bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all transform hover:scale-105 border-2 border-transparent hover:border-blue-500"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <FaUser className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">Apply as Researcher / Professor / Reviewer / EB Member</h3>
                <p className="text-gray-600 text-center mb-4">For PhD scholars, Professors, Reviewers and EB Members</p>
                <div className="flex items-center justify-center text-blue-600 font-semibold group-hover:gap-2 transition-all">
                  <span>Get Started</span>
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                </div>
              </Link>
            </div>

            <div className="text-center">
              <p className="text-gray-600 mb-4">Already have an account?</p>
              <Link
                to="/login"
                className="inline-block px-8 py-3 bg-gradient-to-r from-primary-blue to-secondary-blue text-white font-semibold rounded-lg hover:shadow-lg transition-all"
              >
                Login Here
              </Link>
            </div>
          </div>
        </div>
      ) : (
        // Show actual signup form
        <div className="min-h-screen flex">
          {/* Left Side - Image and Branding */}
          <div className="hidden lg:block lg:w-2/5 fixed left-0 top-0 h-screen">
            <div className="relative w-full h-full">
              <div className="absolute inset-0 bg-black/50 z-10"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-slate-900/60 z-10"></div>
              <video
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              >
                <source src="/istockphoto-1542748041-640_adpp_is.mp4" type="video/mp4" />
              </video>
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-white p-12">
                <img src="/logo.png" alt="Paper Portal" className="h-20 mb-8" />
                <h2 className="text-4xl font-bold mb-4 text-center">Join Us Today!</h2>
                <p className="text-xl text-center max-w-md mb-8">
                  Start your journey in academic publishing with Paper Portal.
                </p>
                <div className="space-y-4 w-full max-w-sm">
                  {signupType === 'young-india' ? (
                    <>
                      <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <FaCheck />
                        </div>
                        <div>
                          <div className="font-semibold text-sm lg:text-base">Upload your Article in 1000 words Max 1200 words</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <FaCheck />
                        </div>
                        <div>
                          <div className="font-semibold text-sm lg:text-base">Upload in PDF Format</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <FaCheck />
                        </div>
                        <div>
                          <div className="font-semibold text-sm lg:text-base">Join our Live Classes with Global Expert Faculty</div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <FaCheck />
                        </div>
                        <div>
                          <div className="font-semibold text-sm lg:text-base">Register and Create ID</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <FaCheck />
                        </div>
                        <div>
                          <div className="font-semibold text-sm lg:text-base">Upload Abstract, Post Approval Upload Your Article</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <FaCheck />
                        </div>
                        <div>
                          <div className="font-semibold text-sm lg:text-base">Get Article Published Online & in National and International Journal</div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Signup Form */}
          <div className="w-full lg:w-3/5 lg:ml-[40%] min-h-screen flex items-center justify-center bg-gradient-to-br from-light-blue to-white p-4 sm:p-6 overflow-y-auto">
            <div className="max-w-3xl w-full py-8">
              <div className="lg:hidden text-center mb-6">
                <img src="/logo.png" alt="Paper Portal" className="h-16 mx-auto mb-4" />
              </div>

              <div className="bg-white rounded-2xl shadow-2xl p-5 sm:p-10">
                <div className="text-center mb-8">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{config.title}</h2>
                  <p className="text-gray-600 mt-2">{config.subtitle}</p>
                  {/* Mobile-only bullet points - shown after subtitle on small screens */}
                  <div className="lg:hidden space-y-3 mt-5 text-left max-w-sm mx-auto">
                    {signupType === 'young-india' ? (
                      <>
                        <div className="flex items-center space-x-3 bg-blue-50 rounded-lg p-3">
                          <div className="w-8 h-8 bg-gradient-to-r from-primary-blue to-secondary-blue rounded-full flex items-center justify-center flex-shrink-0">
                            <FaCheck className="text-white text-xs" />
                          </div>
                          <div className="font-medium text-gray-800 text-sm">Upload your Article in 1000 words Max 1200 words</div>
                        </div>
                        <div className="flex items-center space-x-3 bg-blue-50 rounded-lg p-3">
                          <div className="w-8 h-8 bg-gradient-to-r from-primary-blue to-secondary-blue rounded-full flex items-center justify-center flex-shrink-0">
                            <FaCheck className="text-white text-xs" />
                          </div>
                          <div className="font-medium text-gray-800 text-sm">Upload in PDF Format</div>
                        </div>
                        <div className="flex items-center space-x-3 bg-blue-50 rounded-lg p-3">
                          <div className="w-8 h-8 bg-gradient-to-r from-primary-blue to-secondary-blue rounded-full flex items-center justify-center flex-shrink-0">
                            <FaCheck className="text-white text-xs" />
                          </div>
                          <div className="font-medium text-gray-800 text-sm">Join our Live Classes with Global Expert Faculty</div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center space-x-3 bg-blue-50 rounded-lg p-3">
                          <div className="w-8 h-8 bg-gradient-to-r from-primary-blue to-secondary-blue rounded-full flex items-center justify-center flex-shrink-0">
                            <FaCheck className="text-white text-xs" />
                          </div>
                          <div className="font-medium text-gray-800 text-sm">Register and Create ID</div>
                        </div>
                        <div className="flex items-center space-x-3 bg-blue-50 rounded-lg p-3">
                          <div className="w-8 h-8 bg-gradient-to-r from-primary-blue to-secondary-blue rounded-full flex items-center justify-center flex-shrink-0">
                            <FaCheck className="text-white text-xs" />
                          </div>
                          <div className="font-medium text-gray-800 text-sm">Upload Abstract, Post Approval Upload Your Article</div>
                        </div>
                        <div className="flex items-center space-x-3 bg-blue-50 rounded-lg p-3">
                          <div className="w-8 h-8 bg-gradient-to-r from-primary-blue to-secondary-blue rounded-full flex items-center justify-center flex-shrink-0">
                            <FaCheck className="text-white text-xs" />
                          </div>
                          <div className="font-medium text-gray-800 text-sm">Get Article Published Online & in National and International Journal</div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Academic Status Selection for Professor signup type */}
                  {signupType === 'professor' ? (
                    <div className="animate-fade-in">
                      <label htmlFor="academicStatus" className="block text-sm font-medium text-gray-700 mb-2">
                        Select Role / Specify Your Current Status *
                      </label>
                      <div className="relative">
                        <select
                          id="academicStatus"
                          name="academicStatus"
                          value={formData.academicStatus || ''}
                          onChange={(e) => {
                            const status = e.target.value;
                            let roleCode = status;
                            if (status === 'researcher') roleCode = 'phd';
                            if (status === 'editorial') {
                              // Prefer 'editorial' role if it exists in API, otherwise fallback to 'professor'
                              const hasEditorialRole = roles.some(r => r.code === 'editorial');
                              roleCode = hasEditorialRole ? 'editorial' : 'professor';
                            }

                            const roleObj = roles.find(r => r.code === roleCode);
                            setFormData(prev => ({
                              ...prev,
                              academicStatus: status,
                              role: roleObj ? roleObj.id : prev.role
                            }));

                            // Update URL to match selection
                            const newParams = new URLSearchParams(searchParams);
                            if (status) newParams.set('role', status);
                            else newParams.delete('role');
                            setSearchParams(newParams, { replace: true });
                          }}
                          required
                          className="w-full px-4 pr-10 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue appearance-none bg-white transition-all text-[13px] sm:text-sm"
                        >
                          <option value="">Choose your current status</option>
                          <option value="researcher">Researcher (Ongoing-PhD / Assistant Professor)</option>
                          <option value="professor">Professor</option>
                          <option value="reviewer">Reviewer</option>
                          <option value="editorial">EB Member</option>
                        </select>
                        <FaChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                        Select Role *
                      </label>
                      <div className="relative">
                        <select
                          id="role"
                          name="role"
                          value={formData.role}
                          onChange={(e) => {
                            handleChange(e);
                            const roleId = e.target.value;
                            const roleObj = roles.find(r => r.id === roleId);
                            if (roleObj) {
                              const newParams = new URLSearchParams(searchParams);
                              newParams.set('role', roleObj.code);
                              setSearchParams(newParams, { replace: true });
                            }
                          }}
                          required
                          disabled={isSubmitting || loadingRoles || config.preSelectedRole !== null}
                          className="w-full px-4 pr-10 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue appearance-none bg-white transition-all disabled:opacity-50 disabled:bg-gray-100 text-[13px] sm:text-sm"
                        >
                          <option value="">
                            {loadingRoles ? 'Loading roles...' : 'Choose your role'}
                          </option>
                          {roles.map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.description}
                            </option>
                          ))}
                        </select>
                        {!config.preSelectedRole && (
                          <FaChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                        )}
                      </div>
                    </div>
                  )}

                  {/* 
                  {(() => {
                    const selectedRole = roles.find(r => r.id === formData.role);
                    const isAcademic = selectedRole && ['professor', 'reviewer', 'editorial', 'phd'].includes(selectedRole.code);
                    if (isAcademic) {
                      return (
                        <div className="animate-fade-in">
                          <label htmlFor="designation" className="block text-sm font-medium text-gray-700 mb-2">
                            Designation *
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <FaBriefcase className="text-gray-400" />
                            </div>
                            <input
                              type="text"
                              id="designation"
                              name="designation"
                              value={formData.designation}
                              onChange={handleChange}
                              required
                              className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all"
                              placeholder="Enter your designation"
                            />
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  */}


                  {/* Name and Date of Birth */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <FaUser className="text-gray-400" />
                        </div>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          disabled={isSubmitting}
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all disabled:opacity-50"
                          placeholder="Enter your full name"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
                        Date of Birth *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <FaCalendar className="text-gray-400" />
                        </div>
                        <input
                          type="text"
                          id="dateOfBirth"
                          name="dateOfBirth"
                          value={formData.dateOfBirth}
                          onChange={handleChange}
                          required
                          maxLength="10"
                          placeholder="DD-MM-YYYY"
                          disabled={isSubmitting}
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all disabled:opacity-50"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="relative">
                      <label htmlFor="university" className="block text-sm font-medium text-gray-700 mb-2">
                        University *
                      </label>
                      <input
                        type="text"
                        placeholder="Search & Select University"
                        value={formData.university || searchUniversity}
                        onChange={(e) => {
                          setSearchUniversity(e.target.value);
                          setFormData({ ...formData, university: '' });
                          setShowUniversityDropdown(true);
                        }}
                        onFocus={() => setShowUniversityDropdown(true)}
                        onBlur={() => {
                          // Delay closing to allow clicking on dropdown
                          setTimeout(() => setShowUniversityDropdown(false), 200);
                        }}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all"
                        required
                      />
                      {showUniversityDropdown && (
                        <div className="absolute z-50 w-full top-full left-0 mt-1 bg-white border-2 border-gray-100 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                          {filteredUniversities.length > 0 ? (
                            filteredUniversities.map((u, index) => (
                              <div
                                key={index}
                                className="px-4 py-3 hover:bg-blue-50 cursor-pointer text-sm transition-colors border-b border-gray-50 last:border-0"
                                onMouseDown={() => {
                                  const uName = u.name || u;
                                  const uState = u.state || '';
                                  const uId = u.id || null; // Capture university ID
                                  setFormData({
                                    ...formData,
                                    university: uName,
                                    university_id: uName === 'Other' ? null : uId,
                                    state: uState || formData.state,
                                    otherUniversityName: uName === 'Other' ? '' : formData.otherUniversityName
                                  });
                                  setSearchUniversity(uName);
                                  if (uState) setSearchState(uState);
                                  setShowUniversityDropdown(false);
                                }}
                              >
                                {u.name || u}
                              </div>
                            ))
                          ) : (
                            <div className="px-4 py-3 text-gray-500 text-sm border-b border-gray-50 cursor-pointer hover:bg-blue-50"
                              onMouseDown={() => {
                                setFormData({ ...formData, university: 'Other' });
                                setSearchUniversity('Other');
                                setShowUniversityDropdown(false);
                              }}
                            >
                              Other (Type manually)
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {formData.university === 'Other' && (
                      <div className="animate-fade-in">
                        <label htmlFor="otherUniversityName" className="block text-sm font-medium text-gray-700 mb-2">
                          Specify University *
                        </label>
                        <input
                          type="text"
                          id="otherUniversityName"
                          name="otherUniversityName"
                          value={formData.otherUniversityName}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all"
                          placeholder="Enter university name"
                        />
                      </div>
                    )}

                    <div>
                      <label htmlFor="college" className="block text-sm font-medium text-gray-700 mb-2">
                        College Name
                      </label>
                      <input
                        type="text"
                        id="college"
                        name="college"
                        value={formData.college}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all"
                        placeholder="Enter your college name (optional)"
                      />
                    </div>

                    {/* 
                    <div className="relative">
                      <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                        State *
                      </label>
                      <input
                        type="text"
                        placeholder="Search & Select State"
                        value={formData.state || searchState}
                        onChange={(e) => {
                          setSearchState(e.target.value);
                          setFormData({ ...formData, state: '' });
                          setShowStateDropdown(true);
                        }}
                        onFocus={() => setShowStateDropdown(true)}
                        onBlur={() => {
                          // Delay closing to allow clicking on dropdown
                          setTimeout(() => setShowStateDropdown(false), 200);
                        }}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all"
                        required
                      />
                      {showStateDropdown && (
                        <div className="absolute z-50 w-full top-full left-0 mt-1 bg-white border-2 border-gray-100 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                          {filteredStates.length > 0 ? (
                            filteredStates.map((s, index) => (
                              <div
                                key={index}
                                className="px-4 py-3 hover:bg-blue-50 cursor-pointer text-sm transition-colors border-b border-gray-50 last:border-0"
                                onMouseDown={() => {
                                  setFormData({ ...formData, state: s });
                                  setSearchState(s);
                                  setShowStateDropdown(false);
                                }}
                              >
                                {s}
                              </div>
                            ))
                          ) : (
                            <div className="px-4 py-3 text-gray-500 text-sm">No state found</div>
                          )}
                        </div>
                      )}
                    </div>
                    */}
                  </div>

                  {/* 
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div className="relative">
                      <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-2">
                        Department
                      </label>
                      <input
                        type="text"
                        placeholder="Search & Select Department"
                        value={subjects.find(s => s.id === formData.department)?.name || searchDepartment}
                        onChange={(e) => {
                          setSearchDepartment(e.target.value);
                          setFormData({ ...formData, department: '' });
                          setShowDepartmentDropdown(true);
                        }}
                        onFocus={() => setShowDepartmentDropdown(true)}
                        onBlur={() => setTimeout(() => setShowDepartmentDropdown(false), 200)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all"
                      />
                      {showDepartmentDropdown && (
                        <div className="absolute z-50 w-full top-full left-0 mt-1 bg-white border-2 border-gray-100 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                          {departmentsWithOther.length > 0 ? (
                            departmentsWithOther.map((s, index) => (
                              <div
                                key={index}
                                className="px-4 py-3 hover:bg-blue-50 cursor-pointer text-sm transition-colors border-b border-gray-50 last:border-0"
                                onMouseDown={() => {
                                  setFormData({
                                    ...formData,
                                    department: s.id,
                                    stream: '',
                                    specialization: '',
                                    otherDepartmentName: s.id === 'other' ? '' : formData.otherDepartmentName,
                                    otherStreamName: '',
                                    otherSpecializationName: ''
                                  });
                                  setSearchDepartment(s.name || s);
                                  setSearchStream('');
                                  setSearchSpecialization('');
                                  setShowDepartmentDropdown(false);
                                  loadMasterData(); // Restore all lists for next selection
                                }}
                              >
                                {s.name || s}
                              </div>
                            ))
                          ) : (
                            <div className="px-4 py-3 text-gray-500 text-sm">No department found</div>
                          )}
                        </div>
                      )}
                    </div>

                    {formData.department === 'other' && (
                      <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-5 animate-fade-in bg-gray-50 p-4 rounded-xl border-2 border-dashed border-gray-200">
                        <div>
                          <label htmlFor="otherDepartmentName" className="block text-sm font-medium text-gray-700 mb-2">
                            Department Name *
                          </label>
                          <input
                            type="text"
                            id="otherDepartmentName"
                            name="otherDepartmentName"
                            value={formData.otherDepartmentName}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all bg-white"
                            placeholder="Enter department"
                          />
                        </div>
                        <div>
                          <label htmlFor="otherStreamName" className="block text-sm font-medium text-gray-700 mb-2">
                            Stream Name *
                          </label>
                          <input
                            type="text"
                            id="otherStreamName"
                            name="otherStreamName"
                            value={formData.otherStreamName}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all bg-white"
                            placeholder="Enter stream"
                          />
                        </div>
                        <div>
                          <label htmlFor="otherSpecializationName" className="block text-sm font-medium text-gray-700 mb-2">
                            Specialization Name *
                          </label>
                          <input
                            type="text"
                            id="otherSpecializationName"
                            name="otherSpecializationName"
                            value={formData.otherSpecializationName}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all bg-white"
                            placeholder="Enter specialization"
                          />
                        </div>
                      </div>
                    )}

                    {formData.department !== 'other' && (
                      <>
                        <div className="relative">
                          <label htmlFor="stream" className="block text-sm font-medium text-gray-700 mb-2">
                            Stream
                          </label>
                          <input
                            type="text"
                            placeholder={formData.department ? "Search & Select Stream" : "Select Department First"}
                            value={streams.find(m => m.id === formData.stream)?.name || searchStream}
                            onChange={(e) => {
                              setSearchStream(e.target.value);
                              setFormData({ ...formData, stream: '' });
                              setShowStreamDropdown(true);
                            }}
                            onFocus={() => formData.department && setShowStreamDropdown(true)}
                            onBlur={() => setTimeout(() => setShowStreamDropdown(false), 200)}
                            className={`w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all ${!formData.department ? 'bg-gray-50 cursor-not-allowed text-gray-400' : ''}`}
                            disabled={!formData.department}
                          />
                          {showStreamDropdown && (
                            <div className="absolute z-50 w-full top-full left-0 mt-1 bg-white border-2 border-gray-100 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                              {filteredStreams.length > 0 ? (
                                filteredStreams.map((m, index) => (
                                  <div
                                    key={index}
                                    className="px-4 py-3 hover:bg-blue-50 cursor-pointer text-sm transition-colors border-b border-gray-50 last:border-0"
                                    onMouseDown={() => {
                                      setFormData({
                                        ...formData,
                                        stream: m.id,
                                        specialization: ''
                                      });
                                      setSearchStream(m.name || m);
                                      setSearchSpecialization('');
                                      setShowStreamDropdown(false);
                                      loadMasterData(); // Restore all lists for next selection
                                    }}
                                  >
                                    {m.name || m}
                                  </div>
                                ))
                              ) : (
                                <div className="px-4 py-3 text-gray-500 text-sm">No stream found</div>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="relative">
                          {(() => {
                            const selectedRole = roles.find(r => r.id === formData.role);
                            const isProfessorOrReviewer = selectedRole && ['professor', 'reviewer', 'editorial', 'phd'].includes(selectedRole.code);

                            return (
                              <>
                                <label htmlFor="specialization" className="block text-sm font-medium text-gray-700 mb-2">
                                  Specialization {isProfessorOrReviewer && '(Multi-select)'}
                                </label>
                                <input
                                  type="text"
                                  placeholder={formData.stream ? (isProfessorOrReviewer ? "Search & Add Multiple" : "Search & Select Specialization") : "Select Stream First"}
                                  value={!isProfessorOrReviewer ? (specializations.find(sp => sp.id === formData.specialization)?.name || searchSpecialization) : searchSpecialization}
                                  onChange={(e) => {
                                    setSearchSpecialization(e.target.value);
                                    if (!isProfessorOrReviewer) setFormData({ ...formData, specialization: '' });
                                    setShowSpecializationDropdown(true);
                                  }}
                                  onFocus={() => formData.stream && setShowSpecializationDropdown(true)}
                                  onBlur={() => setTimeout(() => setShowSpecializationDropdown(false), 200)}
                                  className={`w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all ${!formData.stream ? 'bg-gray-50 cursor-not-allowed text-gray-400' : ''}`}
                                  disabled={!formData.stream}
                                />
                                {isProfessorOrReviewer && formData.specializations.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {formData.specializations.map(id => {
                                      const spec = specializations.find(s => s.id === id);
                                      return (
                                        <span key={id} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                          {spec?.name || id}
                                          <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({
                                              ...prev,
                                              specializations: prev.specializations.filter(sid => sid !== id)
                                            }))}
                                            className="ml-1 text-blue-400 hover:text-blue-600"
                                          >
                                            &times;
                                          </button>
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                                {showSpecializationDropdown && (
                                  <div className="absolute z-50 w-full top-full left-0 mt-1 bg-white border-2 border-gray-100 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                    {filteredSpecs.length > 0 ? (
                                      filteredSpecs.map((sp, index) => (
                                        <div
                                          key={index}
                                          className="px-4 py-3 hover:bg-blue-50 cursor-pointer text-sm transition-colors border-b border-gray-50 last:border-0"
                                          onMouseDown={() => {
                                            if (isProfessorOrReviewer) {
                                              if (!formData.specializations.includes(sp.id)) {
                                                setFormData(prev => ({
                                                  ...prev,
                                                  specializations: [...prev.specializations, sp.id]
                                                }));
                                              }
                                              setSearchSpecialization('');
                                            } else {
                                              setFormData({ ...formData, specialization: sp.id });
                                              setSearchSpecialization(sp.name || sp);
                                            }
                                            setShowSpecializationDropdown(false);
                                            loadMasterData(); // Restore all lists for next selection
                                          }}
                                        >
                                          {sp.name || sp}
                                        </div>
                                      ))
                                    ) : (
                                      <div className="px-4 py-3 text-gray-500 text-sm">No specialization found</div>
                                    )}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </>
                    )}
                  </div>
                  */}

                  {/* Phone Number with Verification */}
                  <div>
                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number * <span className="text-red-500">(Verification Required)</span>
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <FaPhone className="text-gray-400" />
                        </div>
                        <input
                          type="tel"
                          id="phoneNumber"
                          name="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={handleChange}
                          required
                          maxLength="10"
                          disabled={isSubmitting}
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue disabled:bg-gray-50 transition-all"
                          placeholder="Enter 10-digit number"
                        />
                      </div>
                      {!verification.phoneVerified && (
                        <button
                          type="button"
                          onClick={handleSendPhoneOtp}
                          disabled={isPhoneLoading || isSubmitting}
                          className="px-6 py-3 bg-gradient-to-r from-primary-blue to-secondary-blue text-white font-medium rounded-lg hover:shadow-lg transition-all whitespace-nowrap disabled:opacity-50"
                        >
                          Send OTP
                        </button>
                      )}
                      {verification.phoneVerified && (
                        <div className="flex items-center px-4 bg-green-50 border-2 border-green-500 rounded-lg">
                          <FaCheck className="text-green-600 text-xl" />
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-blue-600 font-medium">
                      📱 Your phone number can be used for login
                    </p>

                    {verification.phoneOtpSent && !verification.phoneVerified && (
                      <div className="mt-3 flex gap-2 animate-fade-in">
                        <input
                          type="text"
                          value={verification.phoneOtp}
                          onChange={(e) => setVerification({ ...verification, phoneOtp: e.target.value })}
                          maxLength="6"
                          disabled={isPhoneLoading || isSubmitting}
                          className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue disabled:opacity-50"
                          placeholder="Enter 6-digit OTP"
                        />
                        <button
                          type="button"
                          onClick={handleVerifyPhone}
                          disabled={isPhoneLoading || isSubmitting}
                          className="px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-all disabled:opacity-50"
                        >
                          Confirm
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Email with Optional Verification */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email * <span className="text-gray-500 text-xs">(Verification Optional)</span>
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <FaEnvelope className="text-gray-400" />
                        </div>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          disabled={isSubmitting}
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue disabled:bg-gray-50 transition-all"
                          placeholder="Enter your email"
                        />
                      </div>
                      {!verification.emailVerified && (
                        <button
                          type="button"
                          onClick={handleSendEmailOtp}
                          disabled={isEmailLoading || isSubmitting}
                          className="px-6 py-3 bg-gradient-to-r from-primary-blue to-secondary-blue text-white font-medium rounded-lg hover:shadow-lg transition-all whitespace-nowrap disabled:opacity-50"
                        >
                          Send OTP
                        </button>
                      )}
                      {verification.emailVerified && (
                        <div className="flex items-center px-4 bg-green-50 border-2 border-green-500 rounded-lg">
                          <FaCheck className="text-green-600 text-xl" />
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-blue-600 font-medium">
                      ✉️ Your email address can be used for login
                    </p>

                    {verification.emailOtpSent && !verification.emailVerified && (
                      <div className="mt-3 flex gap-2 animate-fade-in">
                        <input
                          type="text"
                          value={verification.emailOtp}
                          onChange={(e) => setVerification({ ...verification, emailOtp: e.target.value })}
                          maxLength="6"
                          disabled={isEmailLoading || isSubmitting}
                          className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue disabled:opacity-50"
                          placeholder="Enter 6-digit OTP"
                        />
                        <button
                          type="button"
                          onClick={handleVerifyEmail}
                          disabled={isEmailLoading || isSubmitting}
                          className="px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-all disabled:opacity-50"
                        >
                          Confirm
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Password Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                        Password *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <FaLock className="text-gray-400" />
                        </div>
                        <input
                          type={showPassword ? "text" : "password"}
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          required
                          minLength="8"
                          disabled={isSubmitting}
                          className="w-full pl-10 pr-12 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all disabled:opacity-50"
                          placeholder="Min. 8 characters"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-primary-blue transition-colors"
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                        Confirm Password *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <FaLock className="text-gray-400" />
                        </div>
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          id="confirmPassword"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          required
                          minLength="8"
                          disabled={isSubmitting}
                          className="w-full pl-10 pr-12 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all disabled:opacity-50"
                          placeholder="Repeat password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-primary-blue transition-colors"
                        >
                          {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Info Note */}
                  <div className="bg-blue-50 border-l-4 border-primary-blue p-4 rounded-lg">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-primary-blue" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-gray-700">
                          <strong>Note:</strong> Phone verification is required. Both your registered phone number and email can be used for login.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 px-4 bg-gradient-to-r from-primary-blue to-secondary-blue text-white text-lg font-semibold rounded-lg hover:shadow-xl transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Creating Account...
                      </>
                    ) : (
                      'Register My Account'
                    )}
                  </button>

                  {/* Login Link - Preserves signup type */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">Already have an account?</span>
                    </div>
                  </div>

                  <div className="text-center">
                    <Link
                      to={loginLink}
                      className="text-primary-blue hover:text-secondary-blue font-semibold text-lg"
                    >
                      Login Here
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Popup for Reviewer/Editorial Board */}
      {showSuccessPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center animate-scale-in">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaCheckCircle className="text-5xl text-green-500 animate-bounce" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Registration Successful!</h2>
            <div className="space-y-4">
              <p className="text-xl text-gray-700 font-medium">Welcome to Paper Portal</p>
              <div className="p-4 bg-blue-50 border-2 border-blue-100 rounded-xl">
                <p className="text-blue-800 font-semibold leading-relaxed">
                  Once admin verified, you will get the rights.
                </p>
              </div>
              <p className="text-gray-500 text-sm italic">
                You will be redirected to the home page in a few seconds...
              </p>
            </div>
            <div className="mt-8 flex justify-center">
              <div className="flex gap-1">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className={`w-2 h-2 rounded-full bg-primary-blue animate-pulse delay-${i * 150}`}></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Signup;